import  { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription
} from "@shadcn/components/ui/card";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@shadcn/components/ui/tabs";
import { REGION_CODES } from '../constants/regions';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@shadcn/components/ui/table";
import { Bar, Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Badge } from "@shadcn/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shadcn/components/ui/tooltip";
import { Info, TrendingUp, TrendingDown, Minus, Calendar } from 'lucide-react';
import { Button } from "@shadcn/components/ui/button";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@shadcn/components/ui/popover";
import { Calendar as CalendarComponent } from "@shadcn/components/ui/calendar";
import { format } from "date-fns";
import superagent from 'superagent';
import { backendUrl } from '@helpers/consts';

// Register required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

// Component to display year-on-year variance
const VarianceIndicator = ({ variance }) => {
  const numericVariance = parseFloat(variance.replace('%', ''));
  
  if (isNaN(numericVariance) || numericVariance === 0) {
    return (
      <Badge variant="outline" className="flex items-center gap-1 text-slate-500">
        <Minus size={14} />
        <span>{variance}</span>
      </Badge>
    );
  }
  
  if (numericVariance > 0) {
    return (
      <Badge variant="outline" className="flex items-center gap-1 text-emerald-600 bg-emerald-50">
        <TrendingUp size={14} />
        <span>{variance}</span>
      </Badge>
    );
  }
  
  return (
    <Badge variant="outline" className="flex items-center gap-1 text-rose-600 bg-rose-50">
      <TrendingDown size={14} />
      <span>{variance}</span>
    </Badge>
  );
};

// Date picker component
const DatePicker = ({ date, onDateChange, isLoading }) => {
  const [isOpen, setIsOpen] = useState(false);

  
  const validDate = date instanceof Date && !isNaN(date) ? date : new Date();

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="w-full sm:w-auto flex items-center justify-between gap-2 bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
          disabled={isLoading}
        >
          <Calendar className="h-4 w-4" />
          <span>{format(validDate, 'dd/MM/yyyy')}</span>
          {isLoading && (
            <div className="w-4 h-4 rounded-full border-2 border-slate-300 border-t-slate-600 animate-spin ml-2"></div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <CalendarComponent
          mode="single"
          selected={validDate}
          defaultMonth={validDate}
          onSelect={(newDate) => {
            if (newDate instanceof Date && !isNaN(newDate)) {
              onDateChange(newDate);
              setIsOpen(false);
            }
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

// Function to generate consistent colors for charts
const generateChartColors = (count) => {
  const baseColors = [
    { bg: 'rgba(99, 102, 241, 0.7)', border: 'rgb(99, 102, 241)' },    // Indigo
    { bg: 'rgba(16, 185, 129, 0.7)', border: 'rgb(16, 185, 129)' },    // Emerald
    { bg: 'rgba(245, 158, 11, 0.7)', border: 'rgb(245, 158, 11)' },    // Amber
    { bg: 'rgba(239, 68, 68, 0.7)', border: 'rgb(239, 68, 68)' },      // Red
    { bg: 'rgba(59, 130, 246, 0.7)', border: 'rgb(59, 130, 246)' },    // Blue
    { bg: 'rgba(168, 85, 247, 0.7)', border: 'rgb(168, 85, 247)' },    // Purple
    { bg: 'rgba(236, 72, 153, 0.7)', border: 'rgb(236, 72, 153)' },    // Pink
    { bg: 'rgba(34, 211, 238, 0.7)', border: 'rgb(34, 211, 238)' },    // Cyan
    { bg: 'rgba(52, 211, 153, 0.7)', border: 'rgb(52, 211, 153)' },    // Teal
    { bg: 'rgba(251, 146, 60, 0.7)', border: 'rgb(251, 146, 60)' },    // Orange
  ];
  
  const colors = {
    backgroundColor: [],
    borderColor: []
  };
  
  for (let i = 0; i < count; i++) {
    const colorIndex = i % baseColors.length;
    colors.backgroundColor.push(baseColors[colorIndex].bg);
    colors.borderColor.push(baseColors[colorIndex].border);
  }
  
  return colors;
};

// Calculate percentages for pie charts
const calculatePercentages = (data) => {
  const total = data.reduce((sum, value) => sum + (value > 0 ? value : 0), 0);
  return data.map(value => ((value > 0 ? value : 0) / total * 100).toFixed(1));
};

const CategoryAnalysis = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date());
  
  const fetchData = async (selectedDate) => {
    setLoading(true);
    try {
      
      const validDate = selectedDate instanceof Date && !isNaN(selectedDate) 
        ? selectedDate 
        : new Date();
        
      const formattedDate = format(validDate, 'yyyy-MM-dd');
      const response = await superagent.get(`${backendUrl}/reports/category-analysis?date=${formattedDate}`)
      
      setData(response.body);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch data when date changes
  useEffect(() => {
    if (date) {
      fetchData(date);
    }
  }, [date]);
  
  // Group data by region
  const groupByRegion = () => {
    const regions = {};
    
    data.forEach(item => {
      if (!regions[item.region]) {
        regions[item.region] = [];
      }
      regions[item.region].push(item);
    });
    
    return Object.keys(regions).sort((a, b) => {
      const order = REGION_CODES;
      return order.indexOf(a) - order.indexOf(b);
    }).reduce((acc, key) => {
      acc[key] = regions[key];
      return acc;
    }, {});
  };
  
  const regions = groupByRegion();
  const regionCodes = Object.keys(regions);
  
  return (
    <div className='page-content'> 
      <div className="bg-slate-50 min-h-screen">
        {loading ? (
          <div className="container mx-auto py-8 px-4 flex justify-center items-center h-[80vh]">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
              <p className="mt-4 text-slate-600">Loading...</p>
            </div>
          </div>
        ) : (
          <div className="container mx-auto py-8 px-4">
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6 transition-all duration-300">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-slate-800 mb-2">Category Performance Analysis</h1>
                  <p className="text-slate-500">
                    Analysis of sales performance and inventory by category and region
                  </p>
                </div>
                <DatePicker date={date} onDateChange={setDate} isLoading={loading} />
              </div>
              
              {regionCodes.length > 0 ? (
                <Tabs defaultValue={regionCodes[0] || "AU"} className="mt-6">
                  <TabsList className="mb-6 bg-slate-100 p-1 rounded-lg w-fit">
                    {regionCodes.map(region => (
                      <TabsTrigger 
                        key={region} 
                        value={region}
                        className="data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all duration-200"
                      >
                        {region}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  
                  {regionCodes.map(region => (
                    <TabsContent key={region} value={region} className="mt-0">
                      <div className="transition-opacity duration-200">
                        <RegionAnalysis data={regions[region]} region={region} />
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              ) : (
                <div className="flex justify-center items-center py-20">
                  <div className="flex flex-col items-center">
                    <p className="text-slate-500 mt-2">No data for the selected date</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const RegionAnalysis = ({ data, region }) => {
  const categories = data.map(item => item.category);
  
  // Generate consistent colors for charts
  const categoryColors = generateChartColors(categories.length);
  
  // Standard chart colors
  const chartColors = {
    stockUnits: {
      bg: 'rgba(99, 102, 241, 0.6)',
      border: 'rgb(99, 102, 241)'
    },
    salesUnits: {
      bg: 'rgba(16, 185, 129, 0.6)',
      border: 'rgb(16, 185, 129)'
    },
    lastYearSales: {
      bg: 'rgba(245, 158, 11, 0.6)',
      border: 'rgb(245, 158, 11)'
    },
    currentCvr: {
      bg: 'rgba(99, 102, 241, 0.6)',
      border: 'rgb(99, 102, 241)'
    },
    upcomingCvr: {
      bg: 'rgba(16, 185, 129, 0.6)',
      border: 'rgb(16, 185, 129)'
    },
    forecast: {
      bg: 'rgba(244, 114, 182, 0.6)',
      border: 'rgb(244, 114, 182)'
    },
    upcomingContainer: {
      bg: 'rgba(251, 146, 60, 0.6)',
      border: 'rgb(251, 146, 60)'
    }
  };
  
  
  const stockUnitsChartData = {
    labels: categories,
    datasets: [
      {
        label: 'Stock Units',
        data: data.map(item => item.stockUnits),
        backgroundColor: chartColors.stockUnits.bg,
        borderColor: chartColors.stockUnits.border,
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };
  
  
  const stockUnitsPieData = {
    labels: categories,
    datasets: [
      {
        data: data.map(item => item.stockUnits > 0 ? item.stockUnits : 0),
        backgroundColor: categoryColors.backgroundColor,
        borderColor: categoryColors.borderColor,
        borderWidth: 1,
      },
    ],
  };
  
  
  const stockUnitsPercentages = calculatePercentages(data.map(item => item.stockUnits));
  
  
  const salesUnitsChartData = {
    labels: categories,
    datasets: [
      {
        label: 'Sales Units',
        data: data.map(item => item.salesUnits),
        backgroundColor: chartColors.salesUnits.bg,
        borderColor: chartColors.salesUnits.border,
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: 'Last Year Sales',
        data: data.map(item => item.lastYearSalesUnits),
        backgroundColor: chartColors.lastYearSales.bg,
        borderColor: chartColors.lastYearSales.border,
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };
  
  // Data for Sales Units pie chart
  const salesUnitsPieData = {
    labels: categories,
    datasets: [
      {
        data: data.map(item => item.salesUnits),
        backgroundColor: categoryColors.backgroundColor,
        borderColor: categoryColors.borderColor,
        borderWidth: 1,
      },
    ],
  };
  
  // Calculate percentages for labels
  const salesUnitsPercentages = calculatePercentages(data.map(item => item.salesUnits));
  
  // Data for CVR chart
  const cvrChartData = {
    labels: categories,
    datasets: [
      {
        label: 'Current CVR',
        data: data.map(item => item.cvr),
        backgroundColor: chartColors.currentCvr.bg,
        borderColor: chartColors.currentCvr.border,
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: 'Upcoming CVR',
        data: data.map(item => item.upcomingCvr),
        backgroundColor: chartColors.upcomingCvr.bg,
        borderColor: chartColors.upcomingCvr.border,
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };
  
  // Get available forecast months
  const availableForecastMonths = new Set();
  data.forEach(item => {
    if (item.forecastByMonth) {
      Object.keys(item.forecastByMonth).forEach(month => availableForecastMonths.add(month));
    }
  });
  
  const forecastMonths = Array.from(availableForecastMonths).sort();

  // Data for Stock + Forecast Units chart
  const stockForecastChartData = {
    labels: categories,
    datasets: [
      {
        label: 'Current Stock Units',
        data: data.map(item => item.stockUnits > 0 ? item.stockUnits : 0),
        backgroundColor: chartColors.stockUnits.bg,
        borderColor: chartColors.stockUnits.border,
        borderWidth: 1,
        borderRadius: 4,
        stack: 'Stack 0', // Separate stack for current stock
      },
      {
        label: 'Upcoming Container',
        data: data.map(item => item.upcomingContainer),
        backgroundColor: chartColors.upcomingContainer.bg,
        borderColor: chartColors.upcomingContainer.border,
        borderWidth: 1,
        borderRadius: {
          topLeft: 4,
          topRight: 4,
          bottomLeft: 0,
          bottomRight: 0
        },
        stack: 'Stack 1', // Stack for upcoming and forecast
      },
      // Add individual forecast months datasets
      ...forecastMonths.map((month, index, array) => {
        // Generate distinct colors for each forecast month
        const hue = (180 + 35 * index) % 360;
        return {
          label: `Forecast ${month}`,
          data: data.map(item => {
            if (item.forecastByMonth && item.forecastByMonth[month]) {
              return item.forecastByMonth[month];
            }
            return 0;
          }),
          backgroundColor: `hsla(${hue}, 80%, 65%, 0.6)`,
          borderColor: `hsla(${hue}, 80%, 55%)`,
          borderWidth: 1,
          borderRadius: index === array.length - 1 ? {
            topLeft: 0,
            topRight: 0,
            bottomLeft: 4,
            bottomRight: 4
          } : 0,
          stack: 'Stack 1', // Stack for upcoming and forecast
        };
      })
    ],
  };
  
  // Bar chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            family: "'Inter', sans-serif",
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1e293b',
        bodyColor: '#475569',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        titleFont: {
          family: "'Inter', sans-serif",
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 12
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toLocaleString();
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
      y: {
        grid: {
          borderDash: [2, 4],
          color: 'rgba(148, 163, 184, 0.2)'
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          },
          callback: function(value) {
            return value.toLocaleString();
          }
        }
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    }
  };
  
  // Pie chart options
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            family: "'Inter', sans-serif",
            size: 10
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1e293b',
        bodyColor: '#475569',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        padding: 10,
        boxPadding: 4,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw.toLocaleString();
            const percentage = context.chart.data.labels.indexOf(label) !== -1
              ? (context.chart.data.datasets[0].data[context.dataIndex] > 0
                ? ` (${context.chart.data.labels.indexOf(label) !== -1 
                  ? stockUnitsPercentages[context.dataIndex] 
                  : salesUnitsPercentages[context.dataIndex]}%)`
                : ' (0%)')
              : '';
            return `${label}: ${value}${percentage}`;
          }
        }
      },
      cutout: '45%'
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-slate-800">{region} Region Analysis</h2>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-flex items-center text-slate-500 text-sm cursor-pointer hover:text-slate-700 transition-colors">
                <Info size={16} className="mr-1" />
                <span>Information</span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-md">
              <p>This chart displays data for the {region} region. Sales Units represent the quantity sold, Stock Units represent the inventory quantity, and CVR (Coverage Ratio) is the ratio of inventory to sales.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      {/* Data table */}
      <div className="transition-all duration-300">
        <Card className="mb-8 overflow-hidden shadow-sm border-slate-200">
          <CardHeader className="bg-slate-50 border-b border-slate-200">
            <CardTitle className="text-lg text-slate-800">Category Performance</CardTitle>
            <CardDescription>Performance details for each category in the {region} region</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead className="font-medium">Category</TableHead>
                    <TableHead className="font-medium text-right">Sales U</TableHead>
                    <TableHead className="font-medium text-right">Stock Units</TableHead>
                    <TableHead className="font-medium text-right">Stock Value</TableHead>
                    <TableHead className="font-medium text-right">CVR</TableHead>
                    <TableHead className="font-medium text-right">Upcoming Container</TableHead>
                    <TableHead className="font-medium text-right">Upcoming CVR</TableHead>
                    <TableHead className="font-medium text-right">LY Sales U</TableHead>
                    <TableHead className="font-medium">Var LY</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((item, index) => (
                    <TableRow 
                      key={index}
                      className="hover:bg-slate-50 transition-colors duration-150"
                    >
                      <TableCell className="font-medium">{item.category}</TableCell>
                      <TableCell className="text-right">{item.salesUnits.toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        <span className={item.stockUnits < 0 ? 'text-rose-600' : ''}>
                          {item.stockUnits.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-slate-500">{item.stockValue || 'N/A'}</TableCell>
                      <TableCell className="text-right">
                        <span className={item.cvr < 0 ? 'text-rose-600' : ''}>
                          {item.cvr}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{item.upcomingContainer.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{item.upcomingCvr}</TableCell>
                      <TableCell className="text-right">{item.lastYearSalesUnits.toLocaleString()}</TableCell>
                      <TableCell>
                        <VarianceIndicator variance={item.varianceLY} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Stock and Forecast Chart */}
      <div className="mb-6">
        <Card className="shadow-sm border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-800">Stock and Forecast Units by Category</CardTitle>
            <CardDescription>
              Comparison between current stock and future inventory projections
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <Bar 
                data={stockForecastChartData} 
                options={{
                  ...chartOptions,
                  scales: {
                    ...chartOptions.scales,
                    x: {
                      ...chartOptions.scales.x,
                      stacked: true
                    },
                    y: {
                      ...chartOptions.scales.y,
                      stacked: true,
                      ticks: {
                        ...chartOptions.scales.y.ticks,
                        callback: function(value) {
                          return value.toLocaleString();
                        }
                      }
                    }
                  },
                  plugins: {
                    ...chartOptions.plugins,
                    tooltip: {
                      ...chartOptions.plugins.tooltip,
                      callbacks: {
                        ...chartOptions.plugins.tooltip.callbacks,
                        footer: function(tooltipItems) {
                          // Group by stack
                          const stackTotals = {};
                          
                          tooltipItems.forEach(function(tooltipItem) {
                            const stackId = tooltipItem.dataset.stack;
                            if (!stackTotals[stackId]) {
                              stackTotals[stackId] = 0;
                            }
                            stackTotals[stackId] += tooltipItem.parsed.y;
                          });
                          
                          // Return formatted totals
                          return Object.entries(stackTotals).map(([stack, total]) => 
                            `${stack === 'Stack 0' ? 'Current Total' : 'Projection Total'}: ${total.toLocaleString()}`
                          );
                        }
                      }
                    }
                  }
                }} 
              />
            </div>
            <div className="mt-4 text-sm text-slate-500">
              <p>
                <strong>Note:</strong> This chart compares current stock units (left column) with projected future inventory (right column).
                The future inventory column is a stacked view combining upcoming container and forecasts for {forecastMonths.join(' and ')} months.
                Hover over any section to see details and totals for each column.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Stock Units Charts */}
      <div className="mb-6">
        <Card className="shadow-sm border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-800">Stock Units by Category</CardTitle>
            <CardDescription>Distribution of inventory across categories</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Bar Chart */}
              <div className="h-80 lg:col-span-2">
                <Bar data={stockUnitsChartData} options={chartOptions} />
              </div>
              
              {/* Pie Chart */}
              <div className="h-64">
                <Doughnut data={stockUnitsPieData} options={pieChartOptions} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Sales Units Charts */}
      <div className="mb-6">
        <Card className="shadow-sm border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-800">Sales by Category</CardTitle>
            <CardDescription>Comparison of current and previous year sales</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Bar Chart */}
              <div className="h-80 lg:col-span-2">
                <Bar data={salesUnitsChartData} options={chartOptions} />
              </div>
              
              {/* Pie Chart */}
              <div className="h-64">
                <Doughnut data={salesUnitsPieData} options={pieChartOptions} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* CVR Analysis Chart */}
      <div className="transition-all duration-300">
        <Card className="shadow-sm border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-800">CVR Analysis by Category</CardTitle>
            <CardDescription>Current and projected Coverage Ratio</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <Bar data={cvrChartData} options={chartOptions} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CategoryAnalysis;
